import { useRef } from "react";
import { Box, Typography } from "@mui/material";
import { useDrop } from "react-dnd";
import { DND_ITEM_TYPES } from "./supplyTableRow";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CancelIcon from "@mui/icons-material/Cancel";

const ACCEPTED_TYPES = [
  DND_ITEM_TYPES.PRODUCT_ROW,
  DND_ITEM_TYPES.HIDDEN_ROW,
  DND_ITEM_TYPES.ADD_PRODUCT_ROW,
];

const dropZoneBase = {
  position: "fixed",
  left: "50%",
  transform: "translateX(-50%)",
  width: 300,
  height: 80,
  borderRadius: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  transition: "all 0.15s ease",
  zIndex: 999,
  pointerEvents: "auto",
  border: "2px dashed white",
};

// Top drop zone - Previous page (fixed, centered on screen)
const PrevPageDropZone = ({ currentPage, onHoverPreviousPage }) => {
  const hoverStartTime = useRef(null);
  const pageChangeTriggered = useRef(false);

  const [{ isOver, isDragging }, drop] = useDrop(
    () => ({
      accept: ACCEPTED_TYPES,
      hover: (item, monitor) => {
        if (!monitor.isOver({ shallow: true })) {
          hoverStartTime.current = null;
          pageChangeTriggered.current = false;
          return;
        }
        if (currentPage <= 1) return;

        if (hoverStartTime.current === null) {
          hoverStartTime.current = Date.now();
          pageChangeTriggered.current = false;
        }

        const hoverDuration = Date.now() - hoverStartTime.current;
        const delay = pageChangeTriggered.current ? 2000 : 300;
        if (hoverDuration >= delay) {
          pageChangeTriggered.current = true;
          hoverStartTime.current = Date.now();
          onHoverPreviousPage?.();
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        isDragging: ACCEPTED_TYPES.includes(monitor.getItemType()),
      }),
    }),
    [currentPage, onHoverPreviousPage],
  );

  // Use prop directly so it hides immediately when page changes (not waiting for unhover)
  if (!isDragging || currentPage <= 1) return null;

  return (
    <Box
      ref={drop}
      sx={{
        ...dropZoneBase,
        top: "20%",
        bgcolor: isOver ? "error.dark" : "primary.main",
        opacity: isOver ? 1 : 0.85,
        boxShadow: isOver ? 6 : 3,
      }}
    >
      <KeyboardArrowUpIcon sx={{ fontSize: 26, color: "white" }} />
      <Typography sx={{ color: "white", fontWeight: "bold", fontSize: 14 }}>
        前ページへ移動
      </Typography>
    </Box>
  );
};

// Bottom drop zone - Next page (fixed, center-right of screen)
const NextPageDropZone = ({ currentPage, maxPage, onHoverNextPage }) => {
  const hoverStartTime = useRef(null);
  const pageChangeTriggered = useRef(false);

  const [{ isOver, isDragging }, drop] = useDrop(
    () => ({
      accept: ACCEPTED_TYPES,
      hover: (item, monitor) => {
        if (!monitor.isOver({ shallow: true })) {
          hoverStartTime.current = null;
          pageChangeTriggered.current = false;
          return;
        }
        if (currentPage >= maxPage) return;

        if (hoverStartTime.current === null) {
          hoverStartTime.current = Date.now();
          pageChangeTriggered.current = false;
        }

        const hoverDuration = Date.now() - hoverStartTime.current;
        const delay = pageChangeTriggered.current ? 2000 : 300;
        if (hoverDuration >= delay) {
          pageChangeTriggered.current = true;
          hoverStartTime.current = Date.now();
          onHoverNextPage?.();
        }
      },
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        isDragging: ACCEPTED_TYPES.includes(monitor.getItemType()),
      }),
    }),
    [currentPage, maxPage, onHoverNextPage],
  );

  // Use prop directly so it hides immediately when page changes (not waiting for unhover)
  if (!isDragging || currentPage >= maxPage) return null;

  return (
    <Box
      ref={drop}
      sx={{
        ...dropZoneBase,
        bottom: 16,
        bgcolor: isOver ? "error.dark" : "primary.main",
        opacity: isOver ? 1 : 0.85,
        boxShadow: isOver ? 6 : 3,
      }}
    >
      <KeyboardArrowDownIcon sx={{ fontSize: 26, color: "white" }} />
      <Typography sx={{ color: "white", fontWeight: "bold", fontSize: 14 }}>
        次ページへ移動
      </Typography>
    </Box>
  );
};

// Cancel drop zone - shown when dragging any item type
const CancelDropZone = () => {
  const CANCEL_TYPES = [
    DND_ITEM_TYPES.PRODUCT_ROW,
    DND_ITEM_TYPES.HIDDEN_ROW,
    DND_ITEM_TYPES.ADD_PRODUCT_ROW,
  ];
  const [{ isOver, isDragging }, drop] = useDrop(
    () => ({
      accept: CANCEL_TYPES,
      drop: () => ({ cancelled: true }),
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        isDragging: CANCEL_TYPES.includes(monitor.getItemType()),
      }),
    }),
    [],
  );

  if (!isDragging) return null;

  return (
    <Box
      ref={drop}
      sx={{
        ...dropZoneBase,
        left: "auto",
        right: 16,
        top: "50%",
        transform: "translateY(-50%)",
        width: 360,
        height: 100,
        bgcolor: isOver ? "error.dark" : "error.main",
        opacity: isOver ? 1 : 0.85,
        boxShadow: isOver ? 6 : 3,
      }}
    >
      <CancelIcon sx={{ fontSize: 30, color: "white" }} />
      <Typography sx={{ color: "white", fontWeight: "bold", fontSize: 16 }}>
        キャンセル
      </Typography>
    </Box>
  );
};

export { PrevPageDropZone, NextPageDropZone, CancelDropZone };
